
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  getUserSubscriptions,
  updateSubscription,
} from "@/services/edgeFunctionWrappers/subscriptionsManagementWrapper";
import { isSessionExpiredError } from "@/services/edgeFunctionWrappers/common/sessionExpired";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Client } from "@/types/types";

interface BlockClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
  isBlocked: boolean;
  /** Rafraîchissement de la liste / fiche après un blocage réellement écrit. */
  onBlockedChange?: () => void;
}

export function BlockClientDialog({
  open,
  onOpenChange,
  client,
  isBlocked,
  onBlockedChange,
}: BlockClientDialogProps) {
  const { toast } = useToast();
  const { t } = useTranslation(["clients", "common"]);
  const clientName = `${client.firstName} ${client.lastName}`;
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    // Validate reason before proceeding
    if (!reason.trim()) {
      setReasonError(t('blockReasonRequired') || "Please provide a reason");
      return;
    }

    // Clear any previous error
    setReasonError("");
    setIsSubmitting(true);

    try {
      // Le statut client est dérivé de l'abonnement le plus récent : c'est
      // donc lui qu'il faut écrire. Sans cela le dialogue n'affichait qu'un
      // toast et l'adhérent gardait tous ses accès.
      const subscriptions = await getUserSubscriptions(client.id, undefined, client.gymId);
      const latest = [...(subscriptions ?? [])].sort(
        (a, b) =>
          new Date(b?.created_at ?? 0).getTime() - new Date(a?.created_at ?? 0).getTime()
      )[0];

      if (!latest?.id) {
        toast({
          title: t('blockFailed', { defaultValue: "Action impossible" }),
          description: t('noSubscriptionToBlock', {
            defaultValue: "Cet adhérent n'a aucun abonnement à bloquer.",
          }),
          variant: "destructive",
        });
        return;
      }

      const updated = await updateSubscription(
        latest.id,
        { status: isBlocked ? "active" : "blocked", notes: reason.trim() },
        client.gymId
      );
      if (!updated) throw new Error("SUBSCRIPTION_UPDATE_FAILED");

      toast({
        title: isBlocked ? t('accountUnblocked') : t('accountBlocked'),
        description: isBlocked
          ? t('accountUnblockedMessage', { name: clientName })
          : t('accountBlockedMessage', { name: clientName }),
        duration: 3000,
      });

      onBlockedChange?.();
      setReason("");
      onOpenChange(false);
    } catch (error) {
      console.error('[BlockClientDialog] update error:', error);
      // Sur une session expirée, invokeWithRetry a déjà lancé la redirection
      // vers /login : un toast d'échec ne ferait que clignoter avant que la
      // vue ne se démonte, sans rien apprendre à l'utilisateur.
      if (!isSessionExpiredError(error)) {
        toast({
          title: t('blockFailed', { defaultValue: "L'opération a échoué" }),
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isBlocked ? t('unblockAccount') : t('blockAccount')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isBlocked 
              ? t('unblockConfirmation', { name: clientName })
              : t('blockConfirmation', { name: clientName })}
            <br />
            <br />
            {isBlocked 
              ? t('unblockDescription') 
              : t('blockDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4">
          <Label htmlFor="blockReason" className="mb-2 block">
            {isBlocked ? t('unblockReason') : t('blockReason')}
          </Label>
          <Textarea
            id="blockReason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              // Clear error when user starts typing
              if (e.target.value.trim()) setReasonError("");
            }}
            placeholder={isBlocked 
              ? t('unblockReasonPlaceholder') || "Enter a reason for unblocking..."
              : t('blockReasonPlaceholder') || "Enter a reason for blocking..."}
            className={reasonError ? "border-red-500" : ""}
          />
          {reasonError && (
            <p className="text-sm text-red-500 mt-1">{reasonError}</p>
          )}
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel', { ns: 'common' })}</AlertDialogCancel>
          <AlertDialogAction 
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault(); // Prevent dialog from closing automatically
              handleConfirm();
            }} 
            className={isBlocked ? "bg-green-600 hover:bg-green-700 text-white" : "bg-destructive text-destructive-foreground"}
          >
            {isBlocked ? t('unblockAccount') : t('blockAccount')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
